/** New version of admin details to phase out view page/review pages created one off for various record types */
import { Dropdown, DropdownItem, Tabs } from '@platform/components';
import { AdminTableProvider } from './AdminTable';
import { Link } from 'wouter';

type DetailsPageProps = {
  tabs: { id: string; content: React.ReactNode }[];
  title: string;
  pageActions?: DetailPageAction[];
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  previewLink?: string;
};

export const AdminDetailPage = ({
  title,
  tabs,
  pageActions,
  isLoading,
  isError,
  isSuccess,
  previewLink,
}: DetailsPageProps) => {
  return (
    // Some tabs may contain simplified admin tables, provider allows sort/pagination state to be wired in
    <AdminTableProvider>
      <div className='px-14 text-gray-500'>
        <div className='flex justify-between items-center mb-4'>
          <h1 className='text-display-sm font-semibold'>
            {title}
            {previewLink && (
              <span className='ml-2 text-sm text-gray-400'>
                [
                <Link className='text-brand-500' to={previewLink}>
                  preview
                </Link>
                ]
              </span>
            )}
          </h1>
          <PageActions actions={pageActions || []} />
        </div>
        {isLoading && <div>Loading...</div>}
        {isError && <div>Error loading data.</div>}
        {isSuccess && <Tabs title={`${title} Details`} items={tabs} variant='side-tabs' />}
      </div>
    </AdminTableProvider>
  );
};

export type DetailPageAction =
  | {
      type: 'link';
      text: string;
      href: string;
      isDanger?: boolean;
    }
  | {
      type: 'button';
      text: string;
      onPress: () => void;
      isDanger?: boolean;
    };

const PageActions = ({ actions }: { actions: DetailPageAction[] }) => {
  return (
    <Dropdown
      type='button'
      buttonProps={{ children: 'Actions', variant: 'tertiary-gray', size: 'sm' }}
      ariaLabel='Page actions'
    >
      {actions.map((action, index) =>
        action.type === 'button' ?
          <DropdownItem
            key={index}
            onAction={() => action.onPress()}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            {action.text}
          </DropdownItem>
        : <DropdownItem
            key={index}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            <Link to={action.href} className='block w-full h-full'>
              {action.text}
            </Link>
          </DropdownItem>,
      )}
    </Dropdown>
  );
};
